import React, { useEffect, useState } from 'react';
import { Button, CircularProgress, Grid, IconButton, InputAdornment, MenuItem, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Search from '@material-ui/icons/Search';
import PropTypes from 'prop-types';
import { Autocomplete } from '@material-ui/lab';
import ClearIcon from '@material-ui/icons/Clear';
import { DateRangePicker } from '../../components/molecules/DateRangePicker.jsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { useGetFormularies } from '../../queries/queryInspection.js';
import { getCurrentUser } from '../../utils/auth.js';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import classNames from 'classnames';
import { SearchInput } from '../molecules/SearchInput.jsx';
import { useGetProjects } from '../../queries/queryProjects.js';
import { palette } from '../../settings/themes/colors.js';
import { isFilterActive, useActiveFilterClasses } from '../molecules/activeFilter.styles';
import { FilterChipList } from '../molecules/FilterChipList';
import { buildInspectionsFilterChips } from './inspectionsFilterChips';
import { getLast12MonthsRange } from '../molecules/dateRangePresets';

const useStyles = makeStyles(Theme => ({
  containerFilter: {
    gap: '10px',
    display: 'flex',
    padding: '0px 20px 10px 20px',
    alignItems: 'center'
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%'
  },
  filterItem: {
    minWidth: '150px'
  },
  clearButton: {
    marginLeft: '10px',
    textTransform: 'Capitalize'
  },
  fieldContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: Theme.spacing(2)
  },
  heading: {
    color: palette.captudataBlue
  },
  searchInput: {
    width: Theme.spacing(60)
  }
}));

export const INSPECTION_TYPE_OPTIONS = ['formulary', 'route'];
export const INSPECTION_STATUS_OPTIONS = [
  { value: true, label: 'published' },
  { value: false, label: 'initial' }
];

export const DEBOUNCE_TIME = 700;

export const RESET_FILTER_VALUES = {
  type: null,
  formularyIds: null,
  startDate: null,
  endDate: null,
  query: null,
  projectIds: null,
  published: null
};

export const getInitialFilterValues = () => ({ ...RESET_FILTER_VALUES, ...getLast12MonthsRange() });

const debouncedEmitSearchChange = debounce((query, filters, handleChanges) => {
  handleChanges({ ...filters, query });
}, DEBOUNCE_TIME);

export const InspectionsFiltersComponent = ({ filters, onFiltersChange, onClearFilters, clientId }) => {
  const classes = useStyles();
  const { activeFilter } = useActiveFilterClasses();
  const { messages: intlMessages } = useIntl();
  const [byProjectQuery, setByProjectQuery] = useState(null);
  const { data: formularies = [], isLoading: isLoadingFormularies } = useGetFormularies({ clientId });

  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const { data: projects = {}, refetch: refetchProjects } = useGetProjects(
    {
      clientId,
      params: {
        query: byProjectQuery,
        only_main_attributes: true
      }
    },
    {
      enabled: !!byProjectQuery,
      onSuccess: () => setIsLoadingProjects(false),
      onSettled: () => setIsLoadingProjects(false)
    }
  );

  const [searchValue, setSearchValue] = useState(filters.query || '');
  const [selectedProjectName, setSelectedProjectName] = useState(null);

  const activeFilters = Object.keys(RESET_FILTER_VALUES).reduce(
    (accumulator, key) => ({ ...accumulator, [key]: isFilterActive(filters[key], RESET_FILTER_VALUES[key]) }),
    {}
  );
  const hasActiveFilters = Object.values(activeFilters).some(Boolean);
  const isDateRangeActive = activeFilters.startDate || activeFilters.endDate;

  const handleChanges = (newFilters = {}) => {
    onFiltersChange(newFilters);
  };

  const handleSearchChange = e => {
    setSearchValue(e.target.value);
    debouncedEmitSearchChange(e.target.value, filters, handleChanges);
  };

  const handleClearSearchInput = () => {
    setSearchValue('');
    handleChanges({ ...filters, query: RESET_FILTER_VALUES.query });
  };

  const handleStatusChange = event => {
    const value = event.target.value;

    handleChanges({
      ...filters,
      published: value === '' ? null : JSON.parse(value)
    });
  };

  const handleTypeChange = event => {
    handleChanges({ ...filters, type: event.target.value });
  };

  const handleSelectProject = project => {
    setSelectedProjectName((project || {}).name || null);
    handleChanges({ ...filters, projectIds: (project || {}).id || RESET_FILTER_VALUES.projectIds });
  };

  const removeFields = fields => {
    debouncedEmitSearchChange.cancel();

    if (fields.includes('query')) setSearchValue('');

    if (fields.includes('projectIds')) setSelectedProjectName(null);

    handleChanges({
      ...filters,
      ...fields.reduce((accumulator, field) => ({ ...accumulator, [field]: RESET_FILTER_VALUES[field] }), {})
    });
  };

  const filterChips = buildInspectionsFilterChips({
    filters,
    defaults: RESET_FILTER_VALUES,
    context: {
      intlMessages,
      projectName: selectedProjectName,
      statusOptions: INSPECTION_STATUS_OPTIONS,
      formularies
    },
    removeFields
  });

  const handleDateRangeChange = dateRange => handleChanges({ ...filters, ...dateRange });

  const handleClearFilters = () => {
    setSearchValue('');
    setSelectedProjectName(null);
    onClearFilters && onClearFilters();
  };

  return (
    <Grid container spacing={2} className={classes.containerFilter}>
      <Grid item className={classes.container}>
        <Typography variant="h6" className={classes.heading}>
          {intlMessages['common.filters']}
        </Typography>
        <Grid item className={classes.fieldContainer}>
          <Grid item>
            <Typography>{intlMessages['inspections.filter.label.search']}</Typography>
          </Grid>
          <Grid item>
            <TextField
              fullWidth
              variant="outlined"
              className={classNames(classes.searchInput, { [activeFilter]: isFilterActive(searchValue, RESET_FILTER_VALUES.query) })}
              value={searchValue || ''}
              onChange={handleSearchChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {searchValue && (
                      <IconButton onClick={handleClearSearchInput} size="small">
                        <ClearIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={4}>
        <SearchInput
          className={activeFilters.projectIds ? activeFilter : undefined}
          value={filters.projectIds}
          options={projects.projects}
          onSearch={query => {
            if (query.trim() === '') {
              setByProjectQuery(null);
              return;
            }
            setByProjectQuery(query);
          }}
          onSelect={handleSelectProject}
          isLoading={isLoadingProjects}
          label={intlMessages['inspections.filter.byProject']}
        />
      </Grid>
      <Grid item xs={2}>
        <DateRangePicker
          className={isDateRangeActive ? activeFilter : undefined}
          value={{
            startDate: filters.startDate,
            endDate: filters.endDate
          }}
          onChange={handleDateRangeChange}
        />
      </Grid>
      <Grid item>
        <TextField
          select
          label={intlMessages['common.status']}
          value={filters.published === null ? '' : String(filters.published)}
          onChange={handleStatusChange}
          variant="outlined"
          className={classNames(classes.filterItem, { [activeFilter]: activeFilters.published })}
        >
          <MenuItem value="">
            <FormattedMessage id="common.all" />
          </MenuItem>
          {INSPECTION_STATUS_OPTIONS.map(option => (
            <MenuItem key={option.label} value={option.value.toString()}>
              <FormattedMessage id={`project.inspection.status.${option.label}`} />
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item>
        <TextField
          select
          label={intlMessages['common.type']}
          value={filters.type || ''}
          onChange={handleTypeChange}
          variant="outlined"
          className={classNames(classes.filterItem, { [activeFilter]: activeFilters.type })}
        >
          <MenuItem value="">
            <FormattedMessage id="common.all" />
          </MenuItem>
          {INSPECTION_TYPE_OPTIONS.map(option => (
            <MenuItem key={option} value={option}>
              <FormattedMessage id={`project.inspections.type.${option}`} />
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={2}>
        <Autocomplete
          disabled={filters.type === INSPECTION_TYPE_OPTIONS[1]}
          className={activeFilters.formularyIds ? activeFilter : undefined}
          value={filters.formularyIds === null || filters.formularyIds === undefined ? null : `${filters.formularyIds}`}
          getOptionSelected={(option, value) => value === `${option.id}`}
          options={formularies}
          getOptionLabel={option => {
            if (typeof option === 'string') {
              return (formularies.find(opt => `${opt.id}` === option) || {}).name || '';
            }

            return option.name || '';
          }}
          loading={isLoadingFormularies}
          onChange={(_, selectedFormularies) => {
            handleChanges({
              ...filters,
              formularyIds: (selectedFormularies || {}).id
            });
          }}
          renderInput={params => {
            let props = {
              ...params,
              label: intlMessages['inspections.search.byFormularies'],
              variant: 'outlined',
              InputLabelProps: { shrink: true }
            };

            if (isLoadingFormularies) {
              props.InputProps = {
                ...params.InputProps,
                endAdornment: (
                  <InputAdornment position="end">
                    <CircularProgress color="inherit" size={20} />
                  </InputAdornment>
                )
              };
            }

            return <TextField {...props} />;
          }}
        />
      </Grid>
      <Grid item>
        <Button variant="outlined" onClick={handleClearFilters} disabled={!hasActiveFilters} className={classes.clearButton}>
          <FormattedMessage id="common.resetFilters" />
        </Button>
      </Grid>
      <Grid item xs={12}>
        <FilterChipList chips={filterChips} />
      </Grid>
    </Grid>
  );
};

InspectionsFiltersComponent.propTypes = {
  filters: PropTypes.shape({
    published: PropTypes.bool,
    type: PropTypes.string,
    formularyIds: PropTypes.number,
    projectIds: PropTypes.number,
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    query: PropTypes.string
  }).isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  clientId: PropTypes.string.isRequired
};

const mapToProps = state => ({ clientId: getCurrentUser(state).client_id });

export const InspectionsFilters = connect(mapToProps)(InspectionsFiltersComponent);
